import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Head from 'next/head';
import clsx from 'clsx';

import {
  selectPopularPageDescription,
  selectPopularProductSummaries,
  selectHeaderDescriptionText,
  selectTopPage,
} from 'lib/util/privateMarketplace';
import { getBreadcrumbsLDJSONString } from 'lib/util/getBreadcrumbsLDJSONObject';
import { TourList } from 'components/PrivateMarketplace/TourList/TourList';
import { Breadcrumbs } from 'components/PrivateMarketplace/Breadcrumbs/Breadcrumbs';
import { getThemeCss } from 'components/Themes';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';
import { appendQueryString } from 'lib/util/appendQueryString';
import { useQueryString } from 'hooks/useQueryString';

type Props = {
  host: string;
};

export const PopularTourList = ({ host }: Props) => {
  const { t } = useTranslation();
  const products = useSelector(selectPopularProductSummaries);
  const description = useSelector(selectPopularPageDescription);
  const headerText = useSelector(selectHeaderDescriptionText);
  const topPage = useSelector(selectTopPage);

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  const bannerImageUrl = topPage?.banner_image_url;

  const title = `${t('Popularity Ranking')} | ${headerText}`;

  const queryString = useQueryString();

  return (
    <>
      <Head>
        <title>{title}</title>
        {description && <meta name="description" content={description} />}
        <meta property="og:title" content={t('Popularity Ranking')} />
        <meta property="og:site_name" content={headerText} />
        {description && <meta property="og:description" content={description} />}
        {bannerImageUrl && <meta property="og:image" content={bannerImageUrl} />}
        <meta property="og:url" content={'TODO'} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: getBreadcrumbsLDJSONString(host, [
              {
                name: t('HOME'),
                relativeLink: '/top',
              },
              {
                name: t('Popularity Ranking'),
              },
            ]),
          }}
        />
      </Head>
      <Breadcrumbs
        pageTitle={t('Popularity Ranking')}
        parentLinks={[
          {
            href: appendQueryString('/top', queryString),
            text: t('HOME'),
          },
        ]}
      />

      <div
        className={clsx('base-ranking', getThemeCssOverride(bookingWidgetTheme, 'base-ranking'))}
      >
        <div className="base-ranking__body">
          <div
            className={clsx('base-ranking__headline', getThemeCss(bookingWidgetTheme, 'base-font'))}
          >
            <div className="maincolor-ic">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
                <rect width="32" height="32" fill="none" />
                <g>
                  <path
                    d="M32,9.53a2.43,2.43,0,1,0-4.18,1.69c-1.65,3.42-4.81,4.62-7.26,3.53-2.18-1-3-3.72-3.26-5.86a2.77,2.77,0,0,0,1.5-2.47,2.8,2.8,0,1,0-5.6,0,2.77,2.77,0,0,0,1.5,2.47c-.27,2.14-1.08,4.9-3.26,5.86C9,15.84,5.83,14.64,4.18,11.22a2.43,2.43,0,1,0-2.06.72l2.8,10.25H27.08l2.8-10.25A2.43,2.43,0,0,0,32,9.53Z"
                    fill="#0094cc"
                  />
                  <rect x="3.56" y="24.61" width="24.88" height="3.77" rx="1.81" fill="#0094cc" />
                </g>
              </svg>
            </div>
            <h1>{t('Popularity Ranking')}</h1>
          </div>
          <p className="base-ranking__description">{description}</p>
        </div>
      </div>
      <TourList
        products={products}
        filterTypes={['CATEGORY', 'FEATURE', 'TAG']}
        displayLabelTypes={['RECOMMENDED', 'RANKING_SPECIFIC', 'PROMOTION']}
        sortDisabled={true}
      />
    </>
  );
};
